import React, { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { Form } from 'react-bootstrap';
import { Icon } from '../../../components/src/components/Icon/Icon';
import { Validator, validators } from './validators';

const isChangeEvent = (thing: any): thing is ChangeEvent<HTMLInputElement> =>
  typeof thing === 'object' && 'target' in thing;
export type FieldError<TFieldName> = { name: TFieldName; errors: string[] };
export type FieldConfig<TValue> = {
  /**
   * The starting value of the field. This is also the value used when the form is reset()
   */
  initialValue?: TValue;
  /**
   * Any validators to be applied to the field
   * @see validators
   */
  validators?: Validator<TValue | undefined> | Array<Validator<TValue | undefined>>;
  /**
   * Display formatter for the field. For example, if your field is numeric but you
   * want it to display as currency, you can apply a currency formatter to the field
   */
  formatValue?: (value: TValue | undefined) => string;
  /**
   * Value parser that converts user-input into to a proper value. For example, if you have
   * a numeric field and you need to parse "$1,000.00" into a number.
   */
  parseValue?: (newValue: string) => TValue | undefined;
};
export type FormConfig = Record<string, FieldConfig<any>>;
export type FormValues<TForm extends FormConfig> = {
  [K in keyof TForm]: TForm[K]['initialValue'];
};
export type FormFieldInstances<TForm extends FormConfig> = {
  [K in keyof TForm]: FieldInstance<K, TForm[K]['initialValue']>;
};

export type FieldInstance<TKey, TValue> = {
  name: TKey;
  value: TValue;
  /**
   * Used as the value prop in conjunction with Form.Control
   */
  controlValue: TValue | '';
  /**
   * The formatted value for display in a label
   */
  formattedValue: string;
  /**
   * The initial value of the field. This is the value that will be used when the form is reset.
   */
  initialValue: TValue;
  /**
   * Sets the initial value of the field (usefull when the initial value is not known on initial render)
   */
  setInitialValue: (newValue: TValue) => void;
  /**
   * Resets the field back to the initial value
   */
  reset: () => void;
  setValue: (newValue: TValue) => void;
  errors: string[] | undefined;
  /**
   * Is true/false when the form has been validated
   * note: If the form has not been validated, this value is `undefined` because
   * we do not yet know it's valid state
   */
  isValid: boolean | undefined;
  /**
   * Is true/false when the form has been validated
   * note: If the form has not been validated, this value is `undefined` because
   * we do not yet know it's valid state
   * note: This is intended to be passed direclty into `Form.Control`'s `isInvalid` prop
   */
  isInvalid: boolean | undefined;
  isChanged: boolean;
  changeHandler: <TEvent extends ChangeEvent<any> | TValue>(options?: {
    parse?: boolean;
  }) => (eventOrValue: TEvent) => void;
  /**
   * Component to display error messages for the field (only rendered when the form has been validated)
   */
  ErrorFeedback: React.FC;
};

/**
 * Creates a validatable form to help with rendering HTML forms.
 * note: see the tests for this hook for usage examples
 *
 */
export function useForm<TForm extends FormConfig>(formConfig: TForm, dependencies: any[] = []) {
  const form = useMemo(() => formConfig, dependencies);
  const [initialValues, setInitialValues] = useState(
    Object.entries(form).reduce(
      (acc, [key, config]) => ({ ...acc, [key]: config.initialValue }),
      {} as FormValues<TForm>
    )
  );
  const [isValidated, setIsValidated] = useState(false);
  const [errors, setErrors] = useState<Array<FieldError<any>>>([]);
  const [values, setValues] = useState(initialValues);

  useEffect(() => {
    const newErrors = Object.keys(form)
      .flatMap((name: keyof TForm) => {
        const config = form[name]!;
        const fieldValidators = config.validators
          ? Array.isArray(config.validators)
            ? config.validators
            : [config.validators]
          : [];
        const fieldErrors = validators.compose(...fieldValidators)(values[name], name as string);
        return { name, errors: fieldErrors };
      })
      .filter(e => e.errors.length);
    setErrors(newErrors);
  }, [values, isValidated]);

  function setFormValue<TKey extends keyof TForm>(name: TKey, newValue: TForm[TKey]['initialValue']) {
    setValues(originalValues => ({ ...originalValues, [name]: newValue }));
  }

  function createFieldInstance<TKey extends keyof TForm>(name: TKey): FieldInstance<TKey, TForm[TKey]['initialValue']> {
    const config = form[name]!;
    const fieldErrors = errors.filter(e => e.name === name).flatMap(e => e.errors);
    const hasErrors = fieldErrors.length > 0;
    const value = values[name];
    return {
      name,
      value,
      setValue: setFormValue.bind(null, name),
      errors: isValidated ? fieldErrors : undefined,
      isValid: isValidated ? !hasErrors : undefined,
      isInvalid: isValidated ? hasErrors : undefined,
      isChanged: initialValues[name] !== value,
      controlValue: value ?? '',
      formattedValue: config.formatValue ? config.formatValue(value) : String(value === undefined ? '' : value),
      initialValue: initialValues[name],
      setInitialValue: (newValue: TForm[TKey]['initialValue']) => {
        setInitialValues(original => ({ ...original, [name]: newValue }));
        setFormValue(name, newValue);
      },
      reset: () => setFormValue(name, initialValues[name]),
      changeHandler:
        (options?: { parse?: boolean }) =>
        (eventOrNewValue: ChangeEvent<HTMLInputElement> | TForm[typeof name]['initialValue']) => {
          const { parse = true } = options || {};
          const newValue = isChangeEvent(eventOrNewValue) ? eventOrNewValue.target.value : eventOrNewValue;
          const parser = config.parseValue;
          setFormValue(name, parse && parser ? parser(newValue) : newValue);
        },
      ErrorFeedback: () => (
        <>
          {fieldErrors.map((e, i) => (
            <Form.Control.Feedback key={e} type="invalid">
              <span id={`errorMsg-${i}`} role="note">
                <Icon variant="notification-circle" /> {e}
              </span>
            </Form.Control.Feedback>
          ))}
        </>
      )
    } as const;
  }

  const fields = Object.keys(form).reduce<FormFieldInstances<TForm>>((acc, name: keyof TForm) => {
    return {
      ...acc,
      [name]: createFieldInstance(name)
    };
  }, {} as FormFieldInstances<TForm>);

  return {
    fields,
    setIsValidated,
    isValidated,
    errors,
    isValid: errors.length === 0,
    reset: () => {
      setInitialValues(iv => {
        setValues(iv);
        return iv;
      });
      setIsValidated(false);
      setErrors([]);
    },
    submitHandler: (callbackWhenValid: () => void) => (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();
      if (errors.length > 0) {
        setIsValidated(true);
        return;
      }
      setIsValidated(false);
      callbackWhenValid();
    }
  };
}
